import { FC, useMemo, useState } from "react";
import { CompetitionConsentNames, CompetitionInputNames } from "../types";
import { ComeAgreeInfo, EduSecetWrapper, FileInput, FileInputWrapper, FormInfoDescription, FormInfoTitle, LegalsLabel } from "shared/modals/styles";
import { useTranslation } from "react-i18next";
import { CommonInput, CommonLabel, FInter, FlexBox, INPUT_TYPES } from "@brg/ui";
import { UniversitySearchBar } from "shared/components/DropdownSearchBar/UniversitySearchBar";
import styled, { useTheme, css } from "styled-components";
import { DamuseumDropdown } from "shared/components/DropdownSearchBar/DamuseumDropdown";
import { RadioBox, RadioCircle, RadioInputSource, RadioLabel } from "shared/components/Radio/styles";
import { Controller } from "react-hook-form";
import { CustomCheckbox } from "./CustomCheckbox";
import { HolderContent } from "shared/components/ImagePreviewSection/ImagePreviewSection";
import { Lang } from "@brg/api";
import { FlexInputContainer } from "../style";
import { addNonBreakingSpaces } from "shared/helpers/addNonBreakingSpaces";

const RadioWrapper = styled(FlexBox)<{ checked?: boolean}>`
    ${({ checked }) => checked && css`
        .radio-circle {
            &:after {
                content: "" !important;
                width: 5px !important;
                height: 5px !important;
                border-radius: 50% !important;
                background: ${({ theme }) => theme.projectEdit.radio.dot.color} !important;
            }
        }
    `};
`;

const Radio = (props: any) => {
    return (
        <RadioWrapper {...props} style={{ cursor: 'pointer' }} className={props.checked ? 'active' : ''}>
            <FlexBox style={{ position: 'relative' }}>
                <RadioInputSource {...props as any} type={'radio'}/>
                <RadioBox {...props as any}>
                    <RadioCircle className={'radio-circle'} {...props as any} />
                    <RadioLabel {...props as any} >
                        {props.title}
                    </RadioLabel>
                </RadioBox>
            </FlexBox>
        </RadioWrapper>
    )
}

export const CompetitionInputComponents: FC<{ fieldName: CompetitionInputNames | CompetitionConsentNames, inputProps: any }> = ({ fieldName, inputProps }) => {
    const { t, i18n: { language } } = useTranslation();
    const [isUploaded, setIsUploaded] = useState(false);
    const theme = useTheme();

    if (fieldName === CompetitionConsentNames.CONSENT_FILE) {
        return (
            <FlexBox direction='column' directionMobile='column' offsetTop={16} offsetLeftMobile={16} offsetRightMobile={16}>
                <FormInfoTitle color={theme.sendProjectsModal.title}>
                Форма согласия на обработку персональных данных <br /> участника творческого конкурса
            </FormInfoTitle>
            <FormInfoDescription style={{ padding: "32px 0 16px 0"}}>
                Если Вам есть 18 лет, <a href="https://drive.google.com/file/d/1x9PBMss7pnj1dwsjGPTMRxjyNRfOABtX/view" target="_blank">скачайте документ</a> и заполните его. Отсканируйте и загрузите заполненный документ.
            </FormInfoDescription>
            <FormInfoDescription>
                Если Вам нет 18 лет, <a href="https://drive.google.com/file/d/1IiTAkxmZagPU11Yw1i6aNAzcLgAjqk76/view" target="_blank">скачайте документ</a>, Ваш законный представитель должен заполнить его. Отсканируйте и загрузите заполненный документ.
            </FormInfoDescription>
            <Controller
                name={CompetitionConsentNames.CONSENT_FILE}
                control={inputProps?.control}
                rules={{
                    validate: (value) => inputProps.validation(value).catch(() => false) as any,
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <FileInputWrapper 
                        style={{ marginTop: "20px" }}
                        customBackgroundColor={theme.projectEdit.imgContainerBg}
                    >
                    <FileInput
                        {...inputProps}
                        onChange={(ev: any) => {
                            if (!!ev?.target?.files?.length) {
                                setIsUploaded(true)
                            }
                            onChange?.(ev)
                        }}
                    />
                    <HolderContent>
                        <FInter.SmallText color={theme.sendProjectsModal.title}>{isUploaded ? 'Файл загружен' : 'Загрузить файл'}</FInter.SmallText>
                    </HolderContent>
                </FileInputWrapper>
                )}
            />
        </FlexBox>
        )
    }

    if (fieldName === CompetitionInputNames.EDUCATION_PLACE) {
        return (
                <Controller
                    name="educationPlace"
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <UniversitySearchBar
                            {...inputProps}
                            {...inputProps?.register}
                            label={t('designer_edit.fields.education_center')}
                            placeholder=''
                            required={inputProps?.requiredLabel}
                            onChange={onChange}
                            value={value}
                        />   
                    )}
                />                                           
        )
    }

    if (fieldName === CompetitionInputNames.COME_AGREE){
        return (
            <FlexBox direction="row">
                <CommonLabel required={inputProps?.requiredLabel} label={t('designer_edit.fields.come_agree')} />
                <FlexBox direction='column' directionMobile='column'>
                    <ComeAgreeInfo className='come-agree-info'>{t('designer_edit.fields.come_agree_info')}</ComeAgreeInfo>
                    <EduSecetWrapper>
                        <DamuseumDropdown 
                            editing={true}
                            onAddClick={(ev:any) => console.log('add', ev)}
                            onChooseClick={(ev: any) => console.log('choose',ev)}
                            // currentValue={comeAgreeTranslate}
                            valueList={language === 'ru' ? ['Да', 'Нет'] : ['Yes', 'No']}
                            // onValueChange={handleUpdateComeAgreeChange}
                            {...inputProps}
                            {...inputProps?.register}
                        />
                    </EduSecetWrapper> 
                </FlexBox>
            </FlexBox>
        )
    }

    if ([CompetitionInputNames.TYPE_OF_AWARD, CompetitionInputNames.NOMINATIONS_LIST, CompetitionInputNames.NOMINATION_NAME]?.includes(fieldName)){ 

        return  (
            <FlexBox direction="row">
                <CommonLabel 
                    required={inputProps?.requiredLabel} 
                    label={t(`designer_edit.fields.${fieldName}`)} 
                />
                <Controller
                    name={fieldName}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value: controlledValue }, fieldState: { error } }) => (
                        <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                            {/*@ts-ignore*/}
                        {inputProps?.options?.map((item, currentIndex, array) => {
                            return (
                                <Radio 
                                    checked={Lang(item?.text, language) === Lang(controlledValue?.text, language)}
                                    title={Lang(item?.text, language)}
                                    {...inputProps}
                                    onChange={(e: any) => {
                                        const newObject = {...array[currentIndex], value: 'true'}
                                        onChange(newObject)
                                    }}

                                />
                            ) 
                        })}
                    </FlexBox>
                    )}
                />
            </FlexBox>
        )
    }

    if (inputProps.type === "radio"){
        return  (
            <FlexBox direction="row">
                <CommonLabel required={inputProps?.requiredLabel} label={t(`designer_edit.fields.${inputProps?.label}`)} />
                <Controller
                    name={inputProps?.id}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value: controlledValue }, fieldState: { error } }) => (
                        <FlexBox direction="row" directionMobile="column" gx={8} gxMobile={8}>
                            {/*@ts-ignore*/}
                            <Radio 
                                checked={!!controlledValue}
                                title={t('designer_edit.fields.yes')}
                                onClick={(e: any) => {
                                    onChange(true)
                                }}
                            />
                            <Radio 
                                checked={!controlledValue}
                                title={t('designer_edit.fields.no')}
                                onClick={(e: any) => {
                                    onChange(false)
                                }}
                            />
                        </FlexBox>
                    )}
                />
        </FlexBox>
        )
    }


    if (inputProps.type === "checkbox"){
        return  (
            <FlexBox direction="row">
                {inputProps?.label && <CommonLabel required={inputProps?.requiredLabel} label={t(`designer_edit.fields.${inputProps?.label}`)} />}
                <Controller
                    name={inputProps?.id}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value = false }, fieldState: { error } }) => {
                        return (
                            <FlexBox direction='row' align='center'>
                                <CustomCheckbox
                                    onChange={onChange}
                                    value={value}
                                    {...inputProps}
                                />
                                <span className="checkbox-label">{inputProps?.checkboxLabel}</span>
                            </FlexBox>
                        )
                    }}
                />
            </FlexBox>
        )
    }

    if (fieldName === CompetitionInputNames.PHONE){
        return (
            <FlexInputContainer direction="row" directionMobile="column">
                <Controller
                    name={inputProps?.name}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value = '' }, fieldState: { error } }) => {
                        return (
                            <CommonInput 
                                {...inputProps}
                                {...inputProps?.register}
                                type={INPUT_TYPES.phone}
                                globalLang={language}
                                onChange={onChange}
                                onBlur={(ev) => null}
                                onFocus={(ev) => null}
                                name={inputProps?.name}
                                value={value ?? '+7'}
                                required={inputProps?.requiredLabel}
                                label={t(`designer_edit.fields.${fieldName}`)}

                            />
                        )
                    }}
                />
            </FlexInputContainer>
        )
    }

    if (fieldName === CompetitionInputNames.REG_NUMBER) {
        const { label: _fieldLabelKey, ...restInputProps } = inputProps;
        const regNumberLabelHtml = addNonBreakingSpaces(
            t(`designer_edit.fields.${fieldName}`).replace(
                /<asavLink>(.*?)<\/asavLink>/g,
                '<a href="https://asav.hse.ru/" target="_blank" rel="noopener noreferrer">$1</a>'
            )
        );

        return (
            <CommonInput
                {...restInputProps}
                {...inputProps?.register}
                label={
                    <span
                        dangerouslySetInnerHTML={{
                            __html: regNumberLabelHtml,
                        }}
                    />
                }
                {...(inputProps?.infoText ? { infoText: t(`designer_edit.tips.${inputProps?.infoText}`)} : {})}
                style={{ ...(!!inputProps?.hidden && { display: "none" }) }}
                required={inputProps?.requiredLabel}
            />
        );
    }

    return (
        <CommonInput 
            {...inputProps}
            {...inputProps?.register}
            label={t(`designer_edit.fields.${fieldName}`)}
            {...(inputProps?.infoText ? { infoText: t(`designer_edit.tips.${inputProps?.infoText}`)} : {})}
            style={{ ...(!!inputProps?.hidden && { display: 'none'})}}
            required={inputProps?.requiredLabel}
        />
    )
}import { FC, useMemo, useState } from "react";
import { CompetitionConsentNames, CompetitionInputNames } from "../types";
import { ComeAgreeInfo, EduSecetWrapper, FileInput, FileInputWrapper, FormInfoDescription, FormInfoTitle, LegalsLabel } from "shared/modals/styles";
import { useTranslation } from "react-i18next";
import { CommonInput, CommonLabel, FInter, FlexBox, INPUT_TYPES } from "@brg/ui";
import { UniversitySearchBar } from "shared/components/DropdownSearchBar/UniversitySearchBar";
import styled, { useTheme, css } from "styled-components";
import { DamuseumDropdown } from "shared/components/DropdownSearchBar/DamuseumDropdown";
import { RadioBox, RadioCircle, RadioInputSource, RadioLabel } from "shared/components/Radio/styles";
import { Controller } from "react-hook-form";
import { CustomCheckbox } from "./CustomCheckbox";
import { HolderContent } from "shared/components/ImagePreviewSection/ImagePreviewSection";
import { Lang } from "@brg/api";
import { FlexInputContainer } from "../style";
import { addNonBreakingSpaces } from "shared/helpers/addNonBreakingSpaces";

const RadioWrapper = styled(FlexBox)<{ checked?: boolean}>`
    ${({ checked }) => checked && css`
        .radio-circle {
            &:after {
                content: "" !important;
                width: 5px !important;
                height: 5px !important;
                border-radius: 50% !important;
                background: ${({ theme }) => theme.projectEdit.radio.dot.color} !important;
            }
        }
    `};
`;

const Radio = (props: any) => {
    return (
        <RadioWrapper {...props} style={{ cursor: 'pointer' }} className={props.checked ? 'active' : ''}>
            <FlexBox style={{ position: 'relative' }}>
                <RadioInputSource {...props as any} type={'radio'}/>
                <RadioBox {...props as any}>
                    <RadioCircle className={'radio-circle'} {...props as any} />
                    <RadioLabel {...props as any} >
                        {props.title}
                    </RadioLabel>
                </RadioBox>
            </FlexBox>
        </RadioWrapper>
    )
}

export const CompetitionInputComponents: FC<{ fieldName: CompetitionInputNames | CompetitionConsentNames, inputProps: any }> = ({ fieldName, inputProps }) => {
    const { t, i18n: { language } } = useTranslation();
    const [isUploaded, setIsUploaded] = useState(false);
    const theme = useTheme();

    if (fieldName === CompetitionConsentNames.CONSENT_FILE) {
        return (
            <FlexBox direction='column' directionMobile='column' offsetTop={16} offsetLeftMobile={16} offsetRightMobile={16}>
                <FormInfoTitle color={theme.sendProjectsModal.title}>
                Форма согласия на обработку персональных данных <br /> участника творческого конкурса
            </FormInfoTitle>
            <FormInfoDescription style={{ padding: "32px 0 16px 0"}}>
                Если Вам есть 18 лет, <a href="https://drive.google.com/file/d/1x9PBMss7pnj1dwsjGPTMRxjyNRfOABtX/view" target="_blank">скачайте документ</a> и заполните его. Отсканируйте и загрузите заполненный документ.
            </FormInfoDescription>
            <FormInfoDescription>
                Если Вам нет 18 лет, <a href="https://drive.google.com/file/d/1IiTAkxmZagPU11Yw1i6aNAzcLgAjqk76/view" target="_blank">скачайте документ</a>, Ваш законный представитель должен заполнить его. Отсканируйте и загрузите заполненный документ.
            </FormInfoDescription>
            <Controller
                name={CompetitionConsentNames.CONSENT_FILE}
                control={inputProps?.control}
                rules={{
                    validate: (value) => inputProps.validation(value).catch(() => false) as any,
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <FileInputWrapper 
                        style={{ marginTop: "20px" }}
                        customBackgroundColor={theme.projectEdit.imgContainerBg}
                    >
                    <FileInput
                        {...inputProps}
                        onChange={(ev: any) => {
                            if (!!ev?.target?.files?.length) {
                                setIsUploaded(true)
                            }
                            onChange?.(ev)
                        }}
                    />
                    <HolderContent>
                        <FInter.SmallText color={theme.sendProjectsModal.title}>{isUploaded ? 'Файл загружен' : 'Загрузить файл'}</FInter.SmallText>
                    </HolderContent>
                </FileInputWrapper>
                )}
            />
        </FlexBox>
        )
    }

    if (fieldName === CompetitionInputNames.EDUCATION_PLACE) {
        return (
                <Controller
                    name="educationPlace"
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <UniversitySearchBar
                            {...inputProps}
                            {...inputProps?.register}
                            label={t('designer_edit.fields.education_center')}
                            placeholder=''
                            required={inputProps?.requiredLabel}
                            onChange={onChange}
                            value={value}
                        />   
                    )}
                />                                           
        )
    }

    if (fieldName === CompetitionInputNames.COME_AGREE){
        return (
            <FlexBox direction="row">
                <CommonLabel required={inputProps?.requiredLabel} label={t('designer_edit.fields.come_agree')} />
                <FlexBox direction='column' directionMobile='column'>
                    <ComeAgreeInfo className='come-agree-info'>{t('designer_edit.fields.come_agree_info')}</ComeAgreeInfo>
                    <EduSecetWrapper>
                        <DamuseumDropdown 
                            editing={true}
                            onAddClick={(ev:any) => console.log('add', ev)}
                            onChooseClick={(ev: any) => console.log('choose',ev)}
                            // currentValue={comeAgreeTranslate}
                            valueList={language === 'ru' ? ['Да', 'Нет'] : ['Yes', 'No']}
                            // onValueChange={handleUpdateComeAgreeChange}
                            {...inputProps}
                            {...inputProps?.register}
                        />
                    </EduSecetWrapper> 
                </FlexBox>
            </FlexBox>
        )
    }

    if ([CompetitionInputNames.TYPE_OF_AWARD, CompetitionInputNames.NOMINATIONS_LIST, CompetitionInputNames.NOMINATION_NAME]?.includes(fieldName)){ 

        return  (
            <FlexBox direction="row">
                <CommonLabel 
                    required={inputProps?.requiredLabel} 
                    label={t(`designer_edit.fields.${fieldName}`)} 
                />
                <Controller
                    name={fieldName}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value: controlledValue }, fieldState: { error } }) => (
                        <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                            {/*@ts-ignore*/}
                        {inputProps?.options?.map((item, currentIndex, array) => {
                            return (
                                <Radio 
                                    checked={Lang(item?.text, language) === Lang(controlledValue?.text, language)}
                                    title={Lang(item?.text, language)}
                                    {...inputProps}
                                    onChange={(e: any) => {
                                        const newObject = {...array[currentIndex], value: 'true'}
                                        onChange(newObject)
                                    }}

                                />
                            ) 
                        })}
                    </FlexBox>
                    )}
                />
            </FlexBox>
        )
    }

    if (inputProps.type === "radio"){
        return  (
            <FlexBox direction="row">
                <CommonLabel required={inputProps?.requiredLabel} label={t(`designer_edit.fields.${inputProps?.label}`)} />
                <Controller
                    name={inputProps?.id}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value: controlledValue }, fieldState: { error } }) => (
                        <FlexBox direction="row" directionMobile="column" gx={8} gxMobile={8}>
                            {/*@ts-ignore*/}
                            <Radio 
                                checked={!!controlledValue}
                                title={t('designer_edit.fields.yes')}
                                onClick={(e: any) => {
                                    onChange(true)
                                }}
                            />
                            <Radio 
                                checked={!controlledValue}
                                title={t('designer_edit.fields.no')}
                                onClick={(e: any) => {
                                    onChange(false)
                                }}
                            />
                        </FlexBox>
                    )}
                />
        </FlexBox>
        )
    }


    if (inputProps.type === "checkbox"){
        return  (
            <FlexBox direction="row">
                {inputProps?.label && <CommonLabel required={inputProps?.requiredLabel} label={t(`designer_edit.fields.${inputProps?.label}`)} />}
                <Controller
                    name={inputProps?.id}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value = false }, fieldState: { error } }) => {
                        return (
                            <FlexBox direction='row' align='center'>
                                <CustomCheckbox
                                    onChange={onChange}
                                    value={value}
                                    {...inputProps}
                                />
                                <span className="checkbox-label">{inputProps?.checkboxLabel}</span>
                            </FlexBox>
                        )
                    }}
                />
            </FlexBox>
        )
    }

    if (fieldName === CompetitionInputNames.PHONE){
        return (
            <FlexInputContainer direction="row" directionMobile="column">
                <Controller
                    name={inputProps?.name}
                    control={inputProps?.control}
                    rules={{
                        validate: (value) => inputProps.validation(value).catch(() => false) as any,
                    }}
                    render={({ field: { onChange, value = '' }, fieldState: { error } }) => {
                        return (
                            <CommonInput 
                                {...inputProps}
                                {...inputProps?.register}
                                type={INPUT_TYPES.phone}
                                globalLang={language}
                                onChange={onChange}
                                onBlur={(ev) => null}
                                onFocus={(ev) => null}
                                name={inputProps?.name}
                                value={value ?? '+7'}
                                required={inputProps?.requiredLabel}
                                label={t(`designer_edit.fields.${fieldName}`)}

                            />
                        )
                    }}
                />
            </FlexInputContainer>
        )
    }

    if (fieldName === CompetitionInputNames.REG_NUMBER) {
        const { label: _fieldLabelKey, ...restInputProps } = inputProps;
        const regNumberLabelHtml = addNonBreakingSpaces(
            t(`designer_edit.fields.${fieldName}`).replace(
                /<asavLink>(.*?)<\/asavLink>/g,
                '<a href="https://asav.hse.ru/" target="_blank" rel="noopener noreferrer">$1</a>'
            )
        );

        return (
            <CommonInput
                {...restInputProps}
                {...inputProps?.register}
                label={
                    <span
                        dangerouslySetInnerHTML={{
                            __html: regNumberLabelHtml,
                        }}
                    />
                }
                {...(inputProps?.infoText ? { infoText: t(`designer_edit.tips.${inputProps?.infoText}`)} : {})}
                style={{ ...(!!inputProps?.hidden && { display: "none" }) }}
                required={inputProps?.requiredLabel}
            />
        );
    }

    return (
        <CommonInput 
            {...inputProps}
            {...inputProps?.register}
            label={t(`designer_edit.fields.${fieldName}`)}
            {...(inputProps?.infoText ? { infoText: t(`designer_edit.tips.${inputProps?.infoText}`)} : {})}
            style={{ ...(!!inputProps?.hidden && { display: 'none'})}}
            required={inputProps?.requiredLabel}
        />
    )
}